import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BadgeCheck, History, ShieldAlert } from "lucide-react";
import { PlatformShell } from "@/components/AppShell";
import {
  EmptyState,
  ErrorBox,
  PageHeader,
  PageLoading,
  Spinner,
  SuccessBox,
  WarnBox,
} from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import {
  api,
  type Application,
  type VerificationAuditEvent,
  type VerificationOperator,
  type VerificationReview,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";

type ReviewDetail = {
  review: VerificationReview;
  audit: VerificationAuditEvent[];
};

export function OperatorVerificationsPage() {
  const { applicationId } = useParams<{ applicationId?: string }>();
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [operator, setOperator] = useState<VerificationOperator | null>(null);
  const [reviews, setReviews] = useState<VerificationReview[]>([]);
  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const [operatorResponse, dataResponse] = await Promise.all([
        api.verificationOperatorMe(),
        applicationId
          ? api.getApplicationVerification(applicationId)
          : api.listApplicationVerifications(status),
      ]);
      setOperator(operatorResponse.data);
      if (applicationId) setDetail(dataResponse.data as ReviewDetail);
      else setReviews(dataResponse.data as VerificationReview[]);
    } catch (caught) {
      setError(caught);
      setOperator(null);
    } finally {
      setLoading(false);
    }
  }, [user, applicationId, status]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    void load();
  }, [authLoading, user, navigate, load]);

  if (authLoading || !user)
    return <PageLoading label="Loading operator workspace…" />;

  return (
    <PlatformShell
      email={user.email}
      onSignOut={signOut}
      operatorRole={operator?.role ?? user.operator_role}
      title="Verification reviews"
      subtitle="Platform operator"
    >
      {loading ? (
        <Spinner label="Loading verification authority…" />
      ) : error ? (
        <div className="space-y-4">
          <PageHeader
            eyebrow="Restricted"
            title="Verification authority"
            description="This surface requires a separately granted verification operator role."
          />
          <ErrorBox error={error} />
        </div>
      ) : applicationId && detail ? (
        <VerificationDetailView detail={detail} onReload={load} />
      ) : (
        <VerificationQueue
          reviews={reviews}
          status={status}
          onStatus={setStatus}
        />
      )}
    </PlatformShell>
  );
}

function VerificationQueue({
  reviews,
  status,
  onStatus,
}: {
  reviews: VerificationReview[];
  status: string;
  onStatus: (status: string) => void;
}) {
  return (
    <div>
      <PageHeader
        eyebrow="Trust & safety"
        title="Application verification"
        description="Review immutable owner snapshots. The public blue badge is controlled only by completed operator decisions."
        actions={
          <select
            className="input min-w-40"
            aria-label="Review status"
            value={status}
            onChange={(event) => onStatus(event.target.value)}
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="cancelled">Cancelled</option>
            <option value="all">All reviews</option>
          </select>
        }
      />
      <div className="mb-5">
        <WarnBox>
          Verification never grants scopes, bypasses consent, relaxes redirect
          validation, or changes Project permissions.
        </WarnBox>
      </div>
      {reviews.length === 0 ? (
        <EmptyState
          title="No reviews in this queue"
          icon={<BadgeCheck className="h-5 w-5" />}
        >
          Choose another status or wait for a Project owner to submit a review.
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="data w-full">
            <thead>
              <tr>
                <th>Application</th>
                <th>Project</th>
                <th>Status</th>
                <th>Requested</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {reviews.map((review) => (
                <tr key={review.id}>
                  <td>
                    <div className="font-medium text-text-primary">
                      {review.application.name}
                    </div>
                    <div className="font-mono text-[11px] text-text-disabled">
                      {review.application.client_id}
                    </div>
                  </td>
                  <td>{review.project_name}</td>
                  <td>
                    <Badge
                      variant={
                        review.status === "pending"
                          ? "warning"
                          : review.status === "approved"
                            ? "success"
                            : "secondary"
                      }
                    >
                      {review.status}
                    </Badge>
                  </td>
                  <td>{formatTime(review.requested_at)}</td>
                  <td>
                    <Link
                      className="text-secondary hover:underline"
                      to={`/dashboard/operator/application-verifications/${review.application_id}`}
                    >
                      Review →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VerificationDetailView({
  detail,
  onReload,
}: {
  detail: ReviewDetail;
  onReload: () => Promise<void>;
}) {
  const { review, audit } = detail;
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState("");

  async function resolve(
    event: FormEvent,
    action: "approve" | "deny" | "revoke",
  ) {
    event.preventDefault();
    if (reason.trim().length < 10 || !password) {
      setError(
        "Enter a reason of at least 10 characters and your current password.",
      );
      return;
    }
    if (
      !window.confirm(
        `${action[0].toUpperCase()}${action.slice(1)} verification for ${review.application.name}? This is an audited platform action.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    setMessage("");
    try {
      await api.resolveApplicationVerification(
        review.application_id,
        action,
        reason,
        password,
      );
      setReason("");
      setPassword("");
      setMessage(`Verification ${action} action completed.`);
      await onReload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  const materialChanges = compareSnapshot(review.snapshot, review.application);
  return (
    <div>
      <PageHeader
        eyebrow="Verification review"
        title={review.application.name}
        description={`Project: ${review.project_name}`}
        actions={
          <>
            <Link
              className="btn-secondary"
              to="/dashboard/operator/application-verifications"
            >
              ← Queue
            </Link>
            <PublicVerificationBadge application={review.application} />
          </>
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      {message ? (
        <div className="mb-4">
          <SuccessBox>{message}</SuccessBox>
        </div>
      ) : null}
      {materialChanges.length > 0 ? (
        <div className="mb-5">
          <WarnBox>
            The live Application differs from the submitted snapshot in:{" "}
            {materialChanges.join(", ")}. Approve only after reviewing the
            current state; a material owner update normally cancels a pending
            review.
          </WarnBox>
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <ApplicationSnapshot
          title="Submitted snapshot"
          application={review.snapshot}
        />
        <ApplicationSnapshot
          title="Current Application"
          application={review.application}
        />
      </div>

      <section className="card mt-6 p-5">
        <div className="mb-4 flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 text-primary-strong" aria-hidden />
          <div>
            <h2 className="font-sans font-semibold text-text-primary">
              Reauthenticated decision
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              Every decision requires the operator's current password and a
              durable reason. Project owners cannot call these actions.
            </p>
          </div>
        </div>
        <form className="space-y-4">
          <div>
            <label className="label" htmlFor="verification-reason">
              Decision reason *
            </label>
            <textarea
              id="verification-reason"
              className="input"
              rows={3}
              minLength={10}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="verification-password">
              Current operator password *
            </label>
            <input
              id="verification-password"
              className="input max-w-md"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {review.status === "pending" ? (
              <>
                <button
                  className="btn-primary"
                  disabled={busy}
                  onClick={(event) => void resolve(event, "approve")}
                >
                  <BadgeCheck className="h-4 w-4" aria-hidden /> Approve
                  verification
                </button>
                <button
                  className="btn-danger"
                  disabled={busy}
                  onClick={(event) => void resolve(event, "deny")}
                >
                  Deny review
                </button>
              </>
            ) : null}
            {review.application.eVerify ? (
              <button
                className="btn-danger"
                disabled={busy}
                onClick={(event) => void resolve(event, "revoke")}
              >
                Revoke verification
              </button>
            ) : null}
          </div>
        </form>
      </section>

      <section className="card mt-6 p-5">
        <div className="mb-4 flex items-center gap-2">
          <History className="h-4 w-4 text-secondary" aria-hidden />
          <h2 className="font-sans font-semibold text-text-primary">
            Immutable verification history
          </h2>
        </div>
        <ol className="space-y-3">
          {audit.map((event) => (
            <li
              key={event.id}
              className="rounded-md border border-border-standard bg-surface-container-low p-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge
                  variant={
                    event.event_type === "approved"
                      ? "success"
                      : event.event_type === "automatic_reset" ||
                          event.event_type === "revoked"
                        ? "danger"
                        : "secondary"
                  }
                >
                  {event.event_type.replace("_", " ")}
                </Badge>
                <time className="font-mono text-[11px] text-text-disabled">
                  {formatTime(event.created_at)}
                </time>
              </div>
              <p className="mt-2 text-sm text-text-secondary">{event.reason}</p>
              <div className="mt-1 text-xs text-text-disabled">
                {event.actor_type} · eVerify {String(event.old_e_verify)} →{" "}
                {String(event.new_e_verify)}
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function ApplicationSnapshot({
  title,
  application,
}: {
  title: string;
  application: Application;
}) {
  return (
    <section className="card p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-sans font-semibold text-text-primary">{title}</h2>
        <PublicVerificationBadge application={application} />
      </div>
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <SnapshotField label="Name" value={application.name} />
        <SnapshotField
          label="Developer"
          value={application.developer_name || "Not set"}
        />
        <SnapshotField
          label="Homepage"
          value={application.homepage_url || "Not set"}
        />
        <SnapshotField
          label="Logo"
          value={application.logo_url || "Fallback"}
        />
        <SnapshotField
          label="Redirects"
          value={application.redirect_uris.join("\n")}
        />
        <SnapshotField
          label="Domains"
          value={application.declared_domains.join("\n") || "None"}
        />
        <SnapshotField
          label="Scopes"
          value={application.allowed_scopes.join(", ")}
        />
        <SnapshotField label="Status" value={application.status} />
      </dl>
    </section>
  );
}

function SnapshotField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="eyebrow">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap break-all text-text-secondary">
        {value}
      </dd>
    </div>
  );
}

function PublicVerificationBadge({
  application,
}: {
  application: Application;
}) {
  return application.eVerify ? (
    <Badge className="gap-1 bg-secondary-fixed text-on-secondary-fixed">
      <BadgeCheck className="h-3.5 w-3.5" aria-hidden /> Verified by TinyBase
    </Badge>
  ) : (
    <Badge variant="secondary">Not verified</Badge>
  );
}

function compareSnapshot(snapshot: Application, live: Application): string[] {
  const fields: Array<keyof Application> = [
    "name",
    "developer_name",
    "homepage_url",
    "logo_url",
    "privacy_policy_url",
    "terms_url",
    "redirect_uris",
    "declared_domains",
    "allowed_scopes",
    "branding",
  ];
  return fields
    .filter(
      (field) =>
        JSON.stringify(snapshot[field]) !== JSON.stringify(live[field]),
    )
    .map((field) => field.replaceAll("_", " "));
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
}
